import React, { ReactNode, useCallback } from "react";

import Drawer, { DrawerProps } from "antd/es/drawer";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";

import consts from "@app/component/preview/styles/_consts.module.scss";
import ResizeHandle from "@app/component/ResizeHandle";
import localSettings from "@app/config/LocalSettings";
import styles from "@app/scene/map/components/sidebar/Sidebar/Sidebar.module.scss";
import { DEFAULT_SIDEBAR_WIDTH } from "@app/util/ui/getDefaultSidebarWidth";
import getPageContentContainer from "@app/util/ui/getPageContentContainer";

interface Props extends DrawerProps {
  children: ReactNode;
  className?: string;
  drawerTitle: string;
  maxWidth?: number;
  menu?: ReactNode;
  minWidth?: number;
  onClose: () => void;
  visible?: boolean;
}

const Sidebar: React.FC<Props> = ({
  children,
  className,
  drawerTitle,
  maxWidth = parseInt(consts.maxPaneWidth, 10),
  menu,
  minWidth = DEFAULT_SIDEBAR_WIDTH,
  onClose,
  visible,
  ...props
}) => {
  const forceRender = useRerenderer();

  const width = localSettings.getRecordPaneWidth();

  const handleResize = useCallback(
    (width) => {
      if (visible) {
        localSettings.setRecordPaneWidth(width, "default", minWidth, maxWidth);
        forceRender();
      }
    },
    [forceRender, maxWidth, minWidth, visible]
  );

  return (
    <Drawer
      className={cn(styles.container, className)}
      closable
      contentWrapperStyle={{ maxWidth: `calc(min(99vw, ${maxWidth}px)` }}
      getContainer={getPageContentContainer()}
      mask={false}
      onClose={onClose}
      placement="right"
      push={{ distance: 0 }}
      title={
        <div className={styles.titleContainer}>
          <span className={styles.title}>
            <Tooltip title={drawerTitle}>{drawerTitle}</Tooltip>
          </span>
          <div>{menu}</div>
        </div>
      }
      visible
      width={width}
      {...props}
    >
      <ResizeHandle
        container={getPageContentContainer()}
        max={maxWidth}
        maxPercent={66}
        min={minWidth}
        onResize={handleResize}
      />
      {children}
    </Drawer>
  );
};

export default Sidebar;
